// ConfirmModal — 1:1 port of legacy `src/ui/ConfirmModal.jsx`.
// Wraps <Modal open={true}> with a "cancel / confirm" footer pair.
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button.js';
import { Modal } from './Modal.js';

/** Minimum comfortable touch target for the opted-in footer actions. */
const NARROW_ACTION_MIN_HEIGHT = 'calc(var(--control-h) + var(--s-4))';

export interface ConfirmModalProps {
  title?: string;
  text: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  /**
   * Forwarded to {@link Modal}: the confirmation tracks the visual viewport and
   * fills a narrow screen instead of sitting in a `min(420px, 100vw - 48px)`
   * sheet. Opting in also raises the footer actions to the same 44 px minimum
   * target as `Modal`'s opted-in close icon. Off by default — every existing
   * consumer keeps its current dimensions, chrome and action sizing.
   */
  narrowFullWidth?: boolean;
}

export function ConfirmModal({
  title,
  text,
  confirmLabel,
  cancelLabel,
  danger,
  onConfirm,
  onClose,
  narrowFullWidth = false,
}: ConfirmModalProps) {
  const { t } = useTranslation('common');
  const resolvedTitle = title ?? t('confirm.title');
  const resolvedConfirmLabel = confirmLabel ?? t('actions.confirm');
  const resolvedCancelLabel = cancelLabel ?? t('actions.cancel');
  // Spread, not `style={undefined}`: default consumers must keep passing no
  // `style` at all, exactly as before this opt-in existed.
  const actionStyle: { style?: CSSProperties } = narrowFullWidth
    ? { style: { minHeight: NARROW_ACTION_MIN_HEIGHT } }
    : {};
  return (
    <Modal
      open={true}
      onClose={onClose}
      title={resolvedTitle}
      width={420}
      narrowFullWidth={narrowFullWidth}
      footer={
        <>
          <Button kind="ghost" onClick={onClose} {...actionStyle}>
            {resolvedCancelLabel}
          </Button>
          <Button
            kind={danger ? 'danger' : 'primary'}
            {...actionStyle}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {resolvedConfirmLabel}
          </Button>
        </>
      }
    >
      <div style={{ color: 'var(--fg-2)', fontSize: 13, lineHeight: 1.55 }}>{text}</div>
    </Modal>
  );
}
